/**
 * Adaptive console shell shared by the Vendor workspace and the Pet-Owner app.
 *
 * Layout (taste-skill / ui-ux-pro-max §9 adaptive-navigation):
 *   • ≥ lg  — fixed left sidebar (brand → nav → account/sign-out) + a slim top
 *             bar holding a collapse toggle, the notification bell and theme
 *             toggle. The sidebar collapses to an icon rail (persisted), like
 *             admin-web's `collapsible="icon"`.
 *   • < lg  — sticky top bar (brand + bell + theme + sign-out) and a fixed
 *             bottom tab bar for primary navigation (≤ 5 items).
 *
 * Test-id contract: the canonical ids the e2e suite drives (it runs at desktop
 * width, sidebar expanded by default) live on the DESKTOP instances — sidebar
 * nav links carry `item.testid`, the sidebar sign-out is `sign-out`, the
 * account line is `session-phone`. Mobile duplicates use `-m` suffixes.
 */
import type { LucideIcon } from 'lucide-react';
import { LogOut, Moon, PanelLeftClose, PanelLeftOpen, Sun, UserRound } from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { NotificationBell } from '@/components/notification-bell';
import { cn } from '@/lib/utils';
import { use_theme_store } from '@/lib/theme-store';
import { use_sidebar_store } from '@/lib/sidebar-store';

export type ShellNavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  testid: string;
};

export function AppShell({
  brandIcon: BrandIcon,
  brandTitle,
  nav,
  phone,
  bookingPath,
  profilePath,
  onSignOut,
  navTestId,
  mainTestId,
  maxWidthClass = 'max-w-6xl',
  banner,
}: {
  brandIcon: LucideIcon;
  brandTitle: string;
  nav: ShellNavItem[];
  phone: string | null;
  bookingPath: string;
  /** When set, the account line + a top-bar avatar link to this profile route. */
  profilePath?: string;
  onSignOut: () => void;
  navTestId: string;
  mainTestId: string;
  maxWidthClass?: string;
  /** Rendered above the routed page on every screen — used for account-wide notices. */
  banner?: React.ReactNode;
}) {
  const dark = use_theme_store((s) => s.dark);
  const toggle_theme = use_theme_store((s) => s.toggle);
  const collapsed = use_sidebar_store((s) => s.collapsed);
  const toggle_sidebar = use_sidebar_store((s) => s.toggle);

  return (
    <div className="min-h-svh bg-background">
      {/* ───────── Desktop sidebar ───────── */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden flex-col border-r-2 border-brand-outline-variant/20 bg-card/70 py-4 backdrop-blur-md transition-[width,padding] duration-normal ease-bounce lg:flex',
          collapsed ? 'w-[4.75rem] px-2' : 'w-64 px-4',
        )}
      >
        <div className={cn('flex items-center gap-2.5 py-1', collapsed ? 'justify-center' : 'px-2')}>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-button accent-gradient text-white shadow-clay-sm">
            <BrandIcon className="h-5 w-5" />
          </span>
          {!collapsed && (
            <span className="truncate font-heading text-base font-bold tracking-tight">
              {brandTitle}
            </span>
          )}
        </div>

        <nav className="mt-6 flex flex-col gap-1.5" data-testid={navTestId}>
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              title={collapsed ? n.label : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-button py-2.5 text-sm font-semibold transition-all duration-fast',
                  collapsed ? 'justify-center px-0' : 'px-3',
                  isActive
                    ? 'bg-primary/10 text-primary shadow-clay-sm'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                )
              }
            >
              <n.icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span className="truncate">{n.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Account + sign-out, kept spatially separate from nav (§9) */}
        <div className="mt-auto flex flex-col gap-2 border-t-2 border-brand-outline-variant/20 pt-4">
          {profilePath ? (
            <NavLink
              to={profilePath}
              data-testid="profile-link"
              title={collapsed ? 'Profile' : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-button py-2 text-sm font-semibold transition-all duration-fast',
                  collapsed ? 'justify-center px-0' : 'px-2',
                  isActive
                    ? 'bg-primary/10 text-primary shadow-clay-sm'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                )
              }
            >
              <UserRound className="h-5 w-5 shrink-0" />
              {!collapsed && (
                <span className="min-w-0 truncate text-xs" data-testid="session-phone">
                  Signed in as{' '}
                  <span className="font-semibold text-foreground">{phone ?? 'unknown'}</span>
                </span>
              )}
            </NavLink>
          ) : (
            !collapsed && (
              <div className="px-2 text-xs text-muted-foreground" data-testid="session-phone">
                Signed in as{' '}
                <span className="font-semibold text-foreground">{phone ?? 'unknown'}</span>
              </div>
            )
          )}
          <Button
            variant="ghost"
            size={collapsed ? 'icon' : 'sm'}
            className={cn(!collapsed && 'justify-start gap-2', 'text-muted-foreground hover:text-foreground')}
            onClick={onSignOut}
            data-testid="sign-out"
            title={collapsed ? 'Sign out' : undefined}
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
            {!collapsed && 'Sign out'}
          </Button>
        </div>
      </aside>

      {/* ───────── Content column ───────── */}
      <div
        className={cn(
          'flex min-h-svh flex-col transition-[padding] duration-normal ease-bounce',
          collapsed ? 'lg:pl-[4.75rem]' : 'lg:pl-64',
        )}
      >
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b-2 border-brand-outline-variant/20 bg-background/80 px-4 backdrop-blur-md lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:inline-flex"
            onClick={toggle_sidebar}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
          {/* Brand (mobile only — sidebar carries it on desktop) */}
          <div className="flex items-center gap-2.5 lg:hidden">
            <span className="flex h-9 w-9 items-center justify-center rounded-button accent-gradient text-white shadow-clay-sm">
              <BrandIcon className="h-5 w-5" />
            </span>
            <span className="font-heading text-base font-bold tracking-tight">{brandTitle}</span>
          </div>

          <div className="ml-auto flex items-center gap-1">
            {profilePath && (
              <Button variant="ghost" size="icon" asChild aria-label="Profile" data-testid="profile-avatar">
                <NavLink to={profilePath}>
                  <UserRound className="h-4 w-4" />
                </NavLink>
              </Button>
            )}
            <NotificationBell booking_path={bookingPath} />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle_theme}
              aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              data-testid="theme-toggle"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={onSignOut}
              aria-label="Sign out"
              data-testid="sign-out-m"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <main
          data-testid={mainTestId}
          className={cn('mx-auto w-full flex-1 px-5 pb-28 pt-6 lg:px-8 lg:pb-10', maxWidthClass)}
        >
          {banner}
          <Outlet />
        </main>
      </div>

      {/* ───────── Mobile bottom tab bar ───────── */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 grid border-t-2 border-brand-outline-variant/20 bg-card/90 pb-[max(0.25rem,env(safe-area-inset-bottom))] backdrop-blur-md lg:hidden"
        style={{ gridTemplateColumns: `repeat(${nav.length}, minmax(0, 1fr))` }}
        aria-label="Primary"
      >
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`${n.testid}-m`}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center gap-1 px-1 py-2.5 text-[11px] font-semibold transition-colors duration-fast',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={cn(
                    'flex h-8 w-14 items-center justify-center rounded-full transition-colors duration-fast',
                    isActive && 'bg-primary/10',
                  )}
                >
                  <n.icon className="h-5 w-5" />
                </span>
                <span className="max-w-full truncate">{n.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
